/**
 * batch-66 voertuigcontrole (FO-489 / spec 189 §D) — welke herstelactie hoort
 * bij een loonstrook waar de voertuig-cascade (FO-444) geen voertuig vond?
 *
 * De controle gokt bewust niet verder (`matchVoertuigVermelding`), maar toonde
 * niet waarom een regel bleef hangen en bood overal dezelfde knop "Koppel en
 * onthoud" (een leer-mapping). Die knop helpt alleen als er meerdere gekoppelde
 * voertuigen passen (NIET_EENDUIDIG). Bij GEEN is er een andere oorzaak:
 *
 *   - er is in de periode geen voertuig aan de medewerker gekoppeld, of
 *   - het kenteken op de strook hoort niet bij een gekoppeld voertuig.
 *
 * In beide gevallen lost een leer-mapping niets op (de cascade raadpleegt hem
 * dan niet); de registratie moet bijgewerkt worden (het voertuig aan de
 * medewerker koppelen), of de strook is fout en de afwijking wordt afgehandeld.
 *
 * Puur: geen DB. De aanroeper levert of het kenteken van de strook in het
 * wagenpark staat.
 */

import { formatKenteken } from './voertuig-utils'

export type LoonstrookVoertuigMatchStatusWaarde = 'MATCH' | 'GEEN' | 'NIET_EENDUIDIG'

export type LoonstrookHerstelActieWaarde =
  /** Kies uit de gekoppelde voertuigen; de keuze wordt als leer-mapping onthouden. */
  | 'LEER_MAPPING'
  /** Koppel het voertuig aan de medewerker (berijder-registratie) en draai opnieuw. */
  | 'KOPPEL_VOERTUIG'
  /** Het kenteken staat niet in het wagenpark: voeg het voertuig eerst toe. */
  | 'VOERTUIG_TOEVOEGEN'

export interface StrookHerstelInput {
  matchStatus: LoonstrookVoertuigMatchStatusWaarde | null
  /** Het (genormaliseerde) kenteken dat op de strook gelezen is, of null. */
  strookKenteken: string | null
  /** Aantal voertuigen dat in de periode aan de medewerker gekoppeld was. */
  aantalKandidaten: number
  /** Het voertuig in het wagenpark met het kenteken van de strook, of null. */
  wagenparkVoertuig: { id: string; kenteken: string } | null
}

export interface StrookHerstel {
  actie: LoonstrookHerstelActieWaarde
  /** Wat de beheerder moet doen, in gewone taal. */
  uitleg: string
  /** Het kenteken van de strook in leesbare vorm (V-65-PXH), of null. */
  kenteken: string | null
  /** Het voertuig waar de koppelknop naartoe gaat, of null (dan zelf kiezen). */
  voertuig: { id: string; kenteken: string } | null
}

/**
 * De herstelactie voor één strook, of null wanneer er niets te herstellen valt
 * (het voertuig is herkend, of de cascade draaide niet).
 */
export function bepaalStrookHerstel(input: StrookHerstelInput): StrookHerstel | null {
  if (input.matchStatus !== 'GEEN' && input.matchStatus !== 'NIET_EENDUIDIG') return null

  if (input.matchStatus === 'NIET_EENDUIDIG') {
    return {
      actie: 'LEER_MAPPING',
      uitleg:
        'Meerdere gekoppelde voertuigen passen bij deze vermelding. Kies het voertuig; STREEP onthoudt de keuze voor deze medewerker en past hem voortaan vanzelf toe.',
      kenteken: null,
      voertuig: null,
    }
  }

  const kenteken = input.strookKenteken ? formatKenteken(input.strookKenteken) : null
  if (kenteken && input.wagenparkVoertuig) {
    return {
      actie: 'KOPPEL_VOERTUIG',
      uitleg:
        input.aantalKandidaten > 0
          ? `Kenteken ${kenteken} staat op de strook, maar dit voertuig is in deze periode niet aan de medewerker gekoppeld. Koppel het voertuig aan de medewerker en draai de controle opnieuw. Klopt de strook niet, handel dan de afwijking af.`
          : `Kenteken ${kenteken} staat op de strook, maar er is in deze periode geen voertuig aan de medewerker gekoppeld. Koppel het voertuig aan de medewerker en draai de controle opnieuw.`,
      kenteken,
      voertuig: {
        id: input.wagenparkVoertuig.id,
        kenteken: formatKenteken(input.wagenparkVoertuig.kenteken),
      },
    }
  }
  if (kenteken) {
    return {
      actie: 'VOERTUIG_TOEVOEGEN',
      uitleg: `Kenteken ${kenteken} staat op de strook, maar niet in het wagenpark. Voeg het voertuig toe en koppel het aan de medewerker, of handel de afwijking af als de strook niet klopt.`,
      kenteken,
      voertuig: null,
    }
  }
  return {
    actie: 'KOPPEL_VOERTUIG',
    uitleg:
      'De strook noemt een auto van de zaak, maar er is in deze periode geen voertuig aan de medewerker gekoppeld. Koppel het voertuig aan de medewerker en draai de controle opnieuw.',
    kenteken: null,
    voertuig: null,
  }
}
